import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { createSubscriptionSession } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { BookOpen, ArrowLeft, ArrowRight, CreditCard, User, Settings, LogOut, Check, RefreshCw, Download, Award, Sparkles, ChevronLeft, Mail } from 'lucide-react';

const MAGAZINE_COVER = '/magazine-cover.jpg';

export default function MagazineLanding() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState({ name: user?.name || '', email: user?.email || '', tax_code: '', address: '', city: '', zip: '', country: 'IT' });
  const [invoice, setInvoice] = useState({ company_name: '', vat_number: '', sdi_code: '' });

  const handleOpenCheckout = () => {
    setStep(1);
    setBilling(b => ({ ...b, name: user?.name || b.name, email: user?.email || b.email }));
    setCheckoutOpen(true);
  };

  const handleSubmit = async () => {
    if (!billing.name || !billing.email) { toast.error('Compila nome ed email'); return; }
    const tc = (billing.tax_code || '').replace(/\s/g, '').toUpperCase();
    if (tc.length !== 16 && tc.length !== 11) {
      toast.error('Codice Fiscale non valido (16 caratteri o 11 cifre P.IVA)');
      return;
    }
    setLoading(true);
    try {
      const res = await createSubscriptionSession({
        origin_url: window.location.origin,
        billing_name: billing.name, billing_email: billing.email,
        billing_address: billing.address, billing_city: billing.city, billing_zip: billing.zip, billing_country: billing.country,
        tax_code: tc,
        // Digital delivery: no shipping address needed. Backend still expects the field; send placeholders.
        shipping_name: billing.name, shipping_address: 'DIGITALE',
        shipping_city: '-', shipping_zip: '-', shipping_country: billing.country,
        company_name: invoice.company_name, vat_number: invoice.vat_number, sdi_code: invoice.sdi_code,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante la creazione dell\'abbonamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="magazine-landing">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b" style={{ backgroundColor: 'var(--bg-dark)', borderColor: 'rgba(96,165,250,0.15)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" style={{ textDecoration: 'none' }} data-testid="magazine-brand-logo">
            <BookOpen className="w-5 h-5" style={{ color: 'var(--accent-light)' }} />
            <span className="text-lg tracking-tight" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-on-dark)', fontWeight: 600 }}>Quintessenza</span>
            <span className="hidden sm:inline text-xs tracking-widest uppercase" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)', fontWeight: 300 }}>Rivista</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <span className="hidden sm:inline text-xs" style={{ color: 'var(--text-muted)' }}>{user.name}</span>
                {user.role === 'admin' && (
                  <button onClick={() => navigate('/admin')} className="text-xs px-2 py-1" style={{ color: 'var(--accent-light)' }} data-testid="admin-nav-link">
                    <Settings className="w-3.5 h-3.5 inline mr-1" />Admin
                  </button>
                )}
                <button onClick={logout} className="p-1.5" style={{ color: 'var(--text-muted)' }} data-testid="magazine-logout-btn">
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/auth?mode=login')} className="text-xs px-3 py-1.5" style={{ color: 'var(--accent-light)' }} data-testid="magazine-login-btn">Accedi</button>
                <button onClick={() => navigate('/auth?mode=register')} className="text-xs px-4 py-1.5 rounded-sm font-medium" style={{ backgroundColor: 'rgba(59,130,246,0.9)', color: '#fff' }} data-testid="magazine-register-btn">
                  <User className="w-3.5 h-3.5 inline mr-1" />Registrati
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-4">
        <Link to="/" className="inline-flex items-center text-xs transition-colors hover:opacity-70" style={{ color: 'var(--text-muted)', textDecoration: 'none' }} data-testid="magazine-back-home">
          <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Torna alla Libreria
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-12" data-testid="magazine-hero">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="flex justify-center order-2 md:order-1">
            <div className="relative">
              <img src={MAGAZINE_COVER} alt="Quintessenza Internazionale"
                className="w-full max-w-md rounded-sm"
                style={{ boxShadow: '0 30px 80px rgba(0,0,0,0.20)' }}
                data-testid="magazine-cover-image" />
              <div className="absolute top-4 left-4 px-3 py-1.5 text-xs rounded-sm"
                style={{ backgroundColor: 'var(--accent)', color: '#fff', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>
                DIGITALE &middot; TRIMESTRALE
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-center order-1 md:order-2">
            <p className="text-xs tracking-[0.25em] uppercase mb-3" style={{ color: 'var(--text-muted)' }}>Quintessenza Edizioni presenta</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-5" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.05 }} data-testid="magazine-title">
              Quintessenza <span style={{ color: 'var(--accent)' }}>Internazionale</span>
            </h1>
            <p className="text-base mb-6 leading-relaxed" style={{ color: 'var(--text-secondary)', fontWeight: 300 }} data-testid="magazine-description">
              La <strong>rivista digitale trimestrale</strong> di riferimento per implantologia, chirurgia, protesi e tutte le discipline odontoiatriche.
              Contenuti scientifici originali, casi clinici e le innovazioni piu recenti del settore, direttamente nella tua casella email.
            </p>

            {/* Pricing tile */}
            <div className="p-5 rounded-sm mb-6" style={{ backgroundColor: 'rgba(37,99,235,0.06)', border: '1px solid rgba(96,165,250,0.25)' }} data-testid="magazine-price-box">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-medium" style={{ fontFamily: 'var(--font-heading)', color: 'var(--accent)' }}>1,00 EUR</span>
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>prima uscita</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Poi <strong style={{ color: 'var(--text-primary)' }}>12,50 EUR</strong> ogni 3 mesi &middot; 4 uscite l&apos;anno
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Puoi disdire in qualsiasi momento
              </p>
            </div>

            <Button onClick={handleOpenCheckout} data-testid="magazine-subscribe-btn"
              className="cta-glow rounded-sm px-10 py-6 text-base font-medium tracking-wide w-full sm:w-auto"
              style={{ backgroundColor: '#3B82F6', color: '#fff', textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>
              Abbonati Ora <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            {/* trust badges */}
            <div className="grid grid-cols-3 gap-2 mt-6 text-center">
              <div className="p-2">
                <Download className="w-4 h-4 mx-auto mb-1" style={{ color: 'var(--accent)' }} />
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>100% Digitale</p>
              </div>
              <div className="p-2">
                <RefreshCw className="w-4 h-4 mx-auto mb-1" style={{ color: 'var(--accent)' }} />
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Disdici quando vuoi</p>
              </div>
              <div className="p-2">
                <Award className="w-4 h-4 mx-auto mb-1" style={{ color: 'var(--accent)' }} />
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Autori riconosciuti</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description section */}
      <section className="py-16" style={{ backgroundColor: 'var(--bg-warm)' }} data-testid="magazine-about">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <p className="text-xs tracking-[0.25em] uppercase mb-3 text-center" style={{ color: 'var(--text-muted)' }}>La rivista</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight mb-8 text-center" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-primary)' }}>
            Un punto di riferimento autorevole
          </h2>
          <div className="space-y-5 text-base leading-relaxed" style={{ color: 'var(--text-secondary)', fontWeight: 300 }}>
            <p>
              La rivista <strong style={{ color: 'var(--text-primary)' }}>Quintessenza Internazionale</strong> pubblica contenuti scientifici
              dell&apos;area implantologica, chirurgica e implanto-protesica, e approfondimenti in tutte le discipline odontoiatriche.
            </p>
            <p>
              Ospita inoltre articoli e contributi scientifici dedicati all&apos;igiene dentale e alla medicina estetica del viso,
              e una sezione dedicata a tutto il Team di Studio &mdash; <em>Team News</em>.
            </p>
            <p>
              E&apos; un punto di riferimento autorevole nelle pubblicazioni odontoiatriche per l&apos;elevata affidabilita scientifica dei suoi contenuti.
              Pubblica articoli scientifici originali e casi clinici di autori rinomati ed emergenti, rubriche di attualita scientifica
              e informazioni aggiornate su soluzioni, sistemi, prodotti innovativi, corsi ed eventi delle aziende leader del mondo del dentale.
            </p>
          </div>
        </div>
      </section>

      {/* What's inside */}
      <section className="py-16 max-w-6xl mx-auto px-5 md:px-10" data-testid="magazine-inside">
        <p className="text-xs tracking-[0.25em] uppercase mb-3 text-center" style={{ color: 'var(--text-muted)' }}>Cosa troverai</p>
        <h2 className="text-3xl sm:text-4xl tracking-tight mb-10 text-center" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-primary)' }}>
          In ogni numero
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { title: 'Implantologia', desc: 'Ricerche originali e protocolli clinici.' },
            { title: 'Chirurgia', desc: 'Interventi documentati passo-passo.' },
            { title: 'Protesi', desc: 'Nuovi materiali e workflow digitali.' },
            { title: 'Igiene & Medicina Estetica', desc: 'Approcci integrati al viso.' },
            { title: 'Casi Clinici', desc: 'Autori italiani e internazionali.' },
            { title: 'Attualita Scientifica', desc: 'Rubriche curate da specialisti.' },
            { title: 'Innovazioni di Prodotto', desc: 'Novita dalle aziende leader.' },
            { title: 'Team News', desc: 'Sezione dedicata allo staff di studio.' },
          ].map((f, i) => (
            <div key={i} className="p-5 rounded-sm border transition-transform hover:-translate-y-0.5"
              style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-white)' }}>
              <Sparkles className="w-4 h-4 mb-3" style={{ color: 'var(--accent)' }} />
              <h3 className="text-sm font-semibold mb-1.5" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>{f.title}</h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works - Digital delivery */}
      <section className="py-16" style={{ backgroundColor: 'var(--bg-warm)' }} data-testid="magazine-how-it-works">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <p className="text-xs tracking-[0.25em] uppercase mb-3 text-center" style={{ color: 'var(--text-muted)' }}>Come funziona</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight mb-3 text-center" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-primary)' }}>
            Una rivista 100% digitale
          </h2>
          <p className="text-sm text-center mb-10 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Nessuna spedizione, nessun ritardo postale. Tutta la rivista, sempre disponibile online.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-sm" style={{ backgroundColor: 'var(--bg-white)', border: '1px solid var(--border-light)' }}>
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-3" style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}>
                <CreditCard className="w-5 h-5" style={{ color: 'var(--accent)' }} />
              </div>
              <h3 className="text-sm font-semibold mb-1.5" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>1. Sottoscrivi</h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Attivi l&apos;abbonamento con un pagamento sicuro Stripe. Prima uscita 1 EUR.</p>
            </div>
            <div className="text-center p-6 rounded-sm" style={{ backgroundColor: 'var(--bg-white)', border: '1px solid var(--border-light)' }}>
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-3" style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}>
                <Mail className="w-5 h-5" style={{ color: 'var(--accent)' }} />
              </div>
              <h3 className="text-sm font-semibold mb-1.5" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>2. Ricevi l&apos;email</h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Riceverai una email da <strong>Quintessenza</strong> con le istruzioni per accedere alla rivista digitale.</p>
            </div>
            <div className="text-center p-6 rounded-sm" style={{ backgroundColor: 'var(--bg-white)', border: '1px solid var(--border-light)' }}>
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-3" style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}>
                <Download className="w-5 h-5" style={{ color: 'var(--accent)' }} />
              </div>
              <h3 className="text-sm font-semibold mb-1.5" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>3. Leggi ovunque</h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Consulta la rivista da PC, tablet o smartphone. Ogni 3 mesi ricevi la nuova uscita.</p>
            </div>
          </div>

          <div className="mt-8 p-4 rounded-sm text-center" style={{ backgroundColor: 'rgba(37,99,235,0.06)', border: '1px solid rgba(96,165,250,0.25)' }}>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              <Mail className="w-3.5 h-3.5 inline mr-1" style={{ color: 'var(--accent)' }} />
              <strong>Importante:</strong> subito dopo l&apos;acquisto riceverai una email da Quintessenza con tutte le modalita di fruizione della rivista digitale. Controlla anche la cartella spam.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16" style={{ backgroundColor: 'var(--bg-dark)' }} data-testid="magazine-cta-band">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-on-dark)' }}>
            Inizia oggi a 1 EUR
          </h2>
          <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }}>
            Prima uscita a 1,00 EUR. Poi 12,50 EUR ogni 3 mesi. Nessun vincolo, disdici quando vuoi.
          </p>
          <Button onClick={handleOpenCheckout} data-testid="magazine-subscribe-btn-2"
            className="cta-glow rounded-sm px-10 py-6 text-base font-medium tracking-wide"
            style={{ backgroundColor: '#3B82F6', color: '#fff' }}>
            Abbonati Ora <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10" style={{ backgroundColor: 'var(--bg-dark)', borderTop: '1px solid rgba(96,165,250,0.15)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 text-center">
          <BookOpen className="w-5 h-5 mx-auto mb-3" style={{ color: 'var(--accent-light)' }} />
          <p className="text-lg tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-on-dark)' }}>Quintessenza Edizioni</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Selezione editoriale esclusiva</p>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 px-4 py-3" style={{ backgroundColor: 'var(--bg-dark)', borderTop: '1px solid rgba(96,165,250,0.2)' }}>
        <Button onClick={handleOpenCheckout} className="w-full rounded-sm py-3 text-sm font-medium cta-glow" style={{ backgroundColor: '#3B82F6', color: '#fff' }} data-testid="magazine-sticky-cta">
          Abbonati a 1 EUR &middot; poi 12,50 EUR/3 mesi
        </Button>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="magazine-checkout-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-heading)' }}>Abbonamento Rivista</DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {step === 1 ? 'Dati di fatturazione e spedizione' : 'Conferma e pagamento'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Summary */}
            <div className="flex gap-3 p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', border: '1px solid var(--border-light)' }} data-testid="magazine-summary-box">
              <img src={MAGAZINE_COVER} alt="Rivista" className="w-14 h-20 object-cover rounded-sm" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Quintessenza Internazionale</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Abbonamento trimestrale</p>
                <div className="mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--accent)', fontWeight: 600 }}>1,00 EUR</span> oggi &middot; poi 12,50 EUR ogni 3 mesi
                </div>
              </div>
            </div>

            {step === 1 && (
              <>
                <div>
                  <p className="text-xs font-medium mb-2 flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Dati di Fatturazione</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2"><Input value={billing.name} onChange={e => setBilling(b => ({ ...b, name: e.target.value }))} placeholder="Nome completo" className="text-sm" data-testid="mag-billing-name" /></div>
                    <div className="col-span-2"><Input type="email" value={billing.email} onChange={e => setBilling(b => ({ ...b, email: e.target.value }))} placeholder="Email" className="text-sm" data-testid="mag-billing-email" /></div>
                    <div className="col-span-2">
                      <Input value={billing.tax_code} onChange={e => setBilling(b => ({ ...b, tax_code: e.target.value.toUpperCase() }))}
                        placeholder="Codice Fiscale *" maxLength={16} className="text-sm uppercase" data-testid="mag-billing-tax-code" required />
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Obbligatorio - 16 caratteri persona fisica / 11 cifre P.IVA</p>
                    </div>
                    <div className="col-span-2"><Input value={billing.address} onChange={e => setBilling(b => ({ ...b, address: e.target.value }))} placeholder="Indirizzo" className="text-sm" data-testid="mag-billing-address" /></div>
                    <Input value={billing.city} onChange={e => setBilling(b => ({ ...b, city: e.target.value }))} placeholder="Citta" className="text-sm" data-testid="mag-billing-city" />
                    <Input value={billing.zip} onChange={e => setBilling(b => ({ ...b, zip: e.target.value }))} placeholder="CAP" className="text-sm" data-testid="mag-billing-zip" />
                  </div>
                </div>

                <div className="p-3 rounded-sm" style={{ backgroundColor: 'rgba(37,99,235,0.06)', border: '1px solid rgba(96,165,250,0.25)' }} data-testid="mag-digital-notice">
                  <p className="text-xs font-medium mb-1 flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
                    <Mail className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} /> Rivista Digitale
                  </p>
                  <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                    Questa rivista e&apos; <strong>100% digitale</strong>: non verra spedito nulla a domicilio.
                    Dopo l&apos;acquisto riceverai una <strong>email da Quintessenza</strong> con le istruzioni per accedere e leggere la rivista online.
                  </p>
                </div>

                <div>
                  <p className="text-xs font-medium mb-1">Fatturazione <span className="font-normal" style={{ color: 'var(--text-muted)' }}>(facoltativo)</span></p>
                  <p className="text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>Compila se hai bisogno della fattura. Lascia vuoto per ricevuta.</p>
                  <div className="grid grid-cols-1 gap-2 p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', border: '1px solid var(--border-light)' }}>
                    <Input value={invoice.company_name} onChange={e => setInvoice(i => ({ ...i, company_name: e.target.value }))} placeholder="Ragione Sociale (opz.)" className="text-sm" data-testid="mag-invoice-company" />
                    <Input value={invoice.vat_number} onChange={e => setInvoice(i => ({ ...i, vat_number: e.target.value }))} placeholder="Partita IVA (opz.)" className="text-sm" data-testid="mag-invoice-vat" />
                    <Input value={invoice.sdi_code} onChange={e => setInvoice(i => ({ ...i, sdi_code: e.target.value }))} placeholder="Codice SDI (opz.)" className="text-sm" data-testid="mag-invoice-sdi" />
                  </div>
                </div>

                <Button onClick={() => setStep(2)} className="w-full rounded-sm cta-glow" style={{ backgroundColor: '#3B82F6', color: '#fff' }} data-testid="mag-next-btn">
                  Continua <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="p-4 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', border: '1px solid var(--border-light)' }} data-testid="mag-review-box">
                  <p className="text-xs font-medium mb-2">Riepilogo</p>
                  <ul className="space-y-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                      <span><strong>Oggi:</strong> addebito di 1,00 EUR (prima uscita)</span></li>
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                      <span><strong>Fra 3 mesi:</strong> addebito automatico di 12,50 EUR, poi ogni 3 mesi</span></li>
                    <li className="flex items-start gap-2"><Mail className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                      <span><strong>Rivista digitale:</strong> riceverai un&apos;email da Quintessenza con le istruzioni per accedere</span></li>
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                      <span>Disdici in qualsiasi momento senza vincoli</span></li>
                  </ul>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-sm text-sm" data-testid="mag-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
                  </Button>
                  <Button onClick={handleSubmit} disabled={loading} className="flex-1 rounded-sm cta-glow text-sm"
                    style={{ backgroundColor: '#3B82F6', color: '#fff' }} data-testid="mag-pay-btn">
                    {loading ? 'Elaborazione...' : 'Paga 1,00 EUR e Attiva'}
                  </Button>
                </div>
                <p className="text-[10px] text-center" style={{ color: 'var(--text-muted)' }}>
                  Pagamento sicuro tramite Stripe. Non conserviamo dati carta.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
